import type { ConversationTurnEntry } from '@/features/events/conversation/state/types'
import type { FormattedAgentActivity } from '@/features/events/activity/utils/types'
import type { FileChanges, NotificationContext } from '@/infra/sse/conversation/payloads'
import { AgentMode, EventType } from '@/index'
import { buildMockEvent } from './mockSessions'

// ---------------------------------------------------------------------------
// Public API — types
// ---------------------------------------------------------------------------

export interface DemoStatusIconConfig {
  readonly type:
    | 'processing'
    | 'bash_blocked'
    | 'question_blocked'
    | 'plan_blocked'
    | 'bash_acknowledged'
    | 'question_acknowledged'
    | 'plan_acknowledged'
    | 'reply'
    | 'hidden'
  readonly pulse: boolean
}

export interface DemoConversationFrame {
  readonly event: ConversationTurnEntry
  readonly bubbleType:
    | 'user_prompt'
    | 'agent_narrative'
    | 'notification'
    | 'file_modification'
    | 'questions'
  readonly statusIcon: DemoStatusIconConfig
  readonly showPause: boolean
  readonly showInject: boolean
  readonly activity: FormattedAgentActivity | null
  readonly agentMode: AgentMode
  readonly agentModeTransition: { readonly from: AgentMode; readonly to: AgentMode } | undefined
  readonly durationMs: number
  readonly selectedOption: number | undefined
}

// ---------------------------------------------------------------------------
// Bash command content
// ---------------------------------------------------------------------------

const BASH_COMMAND = `set -euo pipefail

REDIS_URL="\${REDIS_HOST:-localhost}:\${REDIS_PORT:-6379}"

# Verify Redis connectivity
redis-cli -u "redis://\${REDIS_URL}" ping \\
  && echo "\\u2713 Redis connected at \${REDIS_URL}" \\
  || { echo "\\u2717 Redis unreachable"; exit 1; }

# Run existing middleware tests
npm test -- --grep "middleware" \\
  --reporter spec \\
  --timeout 10000 2>&1 \\
  | tee /tmp/middleware-tests.log`

// ---------------------------------------------------------------------------
// Plan content
// ---------------------------------------------------------------------------

const PLAN_CONTENT = `1. Create \`src/middleware/rateLimiter.ts\`
   - \`RateLimiter\` class with Redis sliding window
   - Configurable limits, 2x for authenticated users

2. Update \`src/app.ts\`
   - Wire rate limiter before route handlers
   - Add Redis client import

3. Run test suite to verify no regressions`

// ---------------------------------------------------------------------------
// File content — Write (Frame 10)
// ---------------------------------------------------------------------------

const RATE_LIMITER_CONTENT = `import { Redis } from 'ioredis'
import type { Request, Response, NextFunction } from 'express'

export class RateLimiter {
  constructor(
    private readonly redis: Redis,
    private readonly windowMs: number,
    private readonly maxRequests: number,
  ) {}

  middleware = async (req: Request, res: Response, next: NextFunction) => {
    const key = \`rate:\${req.ip}\`
    const count = await this.redis.incr(key)
    if (count === 1) await this.redis.pexpire(key, this.windowMs)
    if (count > this.maxRequests) {
      res.set('Retry-After', String(Math.ceil((await this.redis.pttl(key)) / 1000)))
      return res.status(429).json({ error: 'Too many requests' })
    }
    next()
  }
}`

// ---------------------------------------------------------------------------
// File content — Edit (Frame 11) — unified diff hunks
// ---------------------------------------------------------------------------

const APP_TS_UPDATES: FileChanges['updates'] = [
  {
    oldStart: 1,
    oldLines: 3,
    newStart: 1,
    newLines: 5,
    lines: [
      " import express from 'express'",
      " import { authMiddleware } from './middleware/auth'",
      "+import { RateLimiter } from './middleware/rateLimiter'",
      "+import { redis } from './config/redis'",
      " import { apiRouter } from './routes/api'",
    ],
  },
  {
    oldStart: 5,
    oldLines: 3,
    newStart: 7,
    newLines: 5,
    lines: [
      ' const app = express()',
      ' ',
      "-app.use('/api/v1', authMiddleware, apiRouter)",
      '+const limiter = new RateLimiter(redis, 60_000, 100)',
      '+',
      "+app.use('/api/v1', limiter.middleware, authMiddleware, apiRouter)",
    ],
  },
]

// ---------------------------------------------------------------------------
// Notification contexts
// ---------------------------------------------------------------------------

function buildBashContext(): NotificationContext {
  return {
    toolName: 'Bash',
    toolInput: {
      command: BASH_COMMAND,
      description: 'Verify Redis connectivity and run middleware smoke test',
    },
    agentDialogue: {
      agentPrompt: 'Claude wants to execute this command',
      userOptions: [
        { index: 1, option: 'Yes' },
        { index: 2, option: 'Yes, allow bash from this project' },
        { index: 3, option: 'No' },
      ],
    },
  }
}

function buildPlanContext(): NotificationContext {
  return {
    toolName: 'ExitPlanMode',
    toolInput: { plan: PLAN_CONTENT },
    agentDialogue: {
      agentPrompt: 'Claude Code wants your approval',
      userOptions: [
        { index: 1, option: 'Yes, clear context and auto-accept edits' },
        { index: 2, option: 'Yes, auto-accept edits' },
        { index: 3, option: 'Yes, manually approve edits' },
        { index: 4, option: 'Type here to tell Claude what to change' },
      ],
    },
  }
}

function buildWriteFileChanges(): FileChanges {
  return {
    targetPath: 'src/middleware/rateLimiter.ts',
    newContent: RATE_LIMITER_CONTENT,
    updates: null,
  }
}

function buildEditFileChanges(): FileChanges {
  return {
    targetPath: 'src/app.ts',
    newContent: null,
    updates: APP_TS_UPDATES,
  }
}

// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------

const GLOBBING_ACTIVITY: FormattedAgentActivity = {
  toolName: 'Glob',
  activityContext: 'src/middleware/**/*.ts',
  status: 'pre_tool',
  agentMode: AgentMode.DEFAULT_MODE,
}

const BASHING_ACTIVITY: FormattedAgentActivity = {
  toolName: 'Bash',
  activityContext: 'redis-cli ping',
  status: 'pre_tool',
  agentMode: AgentMode.DEFAULT_MODE,
}

const WEB_SEARCHING_ACTIVITY: FormattedAgentActivity = {
  toolName: 'WebSearch',
  activityContext: 'redis sliding window rate limit',
  status: 'pre_tool',
  agentMode: AgentMode.DEFAULT_MODE,
}

const WRITING_ACTIVITY: FormattedAgentActivity = {
  toolName: 'Write',
  activityContext: 'rateLimiter.ts',
  status: 'pre_tool',
  agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
  suffix: '+18',
}

const EDITING_ACTIVITY: FormattedAgentActivity = {
  toolName: 'Edit',
  activityContext: 'app.ts',
  status: 'pre_tool',
  agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
  suffix: '+7 -2',
}

// ---------------------------------------------------------------------------
// Frame sequence
// ---------------------------------------------------------------------------

const RESET_PAUSE_MS = 3000

export const CONVERSATION_DEMO_FRAMES: readonly DemoConversationFrame[] = [
  // Frame 1 — User Prompt
  {
    event: buildMockEvent({
      eventType: EventType.USER_PROMPT,
      agentMode: AgentMode.DEFAULT_MODE,
      userPrompt:
        'Add rate limiting to the `/api/v1` endpoints. Use Redis for a sliding window counter \u2014 100 requests per minute per IP. Return `429` with a `Retry-After` header when exceeded.',
    }),
    bubbleType: 'user_prompt',
    statusIcon: { type: 'processing', pulse: true },
    showPause: false,
    showInject: false,
    activity: null,
    agentMode: AgentMode.DEFAULT_MODE,
    agentModeTransition: undefined,
    durationMs: 4000,
    selectedOption: undefined,
  },

  // Frame 2 — Agent Narrative (approach)
  {
    event: buildMockEvent({
      eventType: EventType.AGENT_NARRATIVE,
      agentMode: AgentMode.DEFAULT_MODE,
      agentResponse: `I'll implement Redis-backed rate limiting for the API. Let me examine the current middleware stack and verify connectivity.

**Approach:**
- Sliding window counter using Redis \`INCR\` + \`EXPIRE\`
- Custom \`RateLimiter\` class with configurable limits
- \`429 Too Many Requests\` with \`Retry-After\` header`,
    }),
    bubbleType: 'agent_narrative',
    statusIcon: { type: 'processing', pulse: true },
    showPause: true,
    showInject: true,
    activity: GLOBBING_ACTIVITY,
    agentMode: AgentMode.DEFAULT_MODE,
    agentModeTransition: undefined,
    durationMs: 4500,
    selectedOption: undefined,
  },

  // Frame 3 — Bash Permission
  {
    event: buildMockEvent({
      eventType: EventType.TOOL_PERMISSION_REQUEST,
      agentMode: AgentMode.DEFAULT_MODE,
      notificationMessage: 'Claude wants to execute this command',
      toolName: 'Bash',
      notificationContext: buildBashContext(),
    }),
    bubbleType: 'notification',
    statusIcon: { type: 'bash_blocked', pulse: true },
    showPause: false,
    showInject: false,
    activity: null,
    agentMode: AgentMode.DEFAULT_MODE,
    agentModeTransition: undefined,
    durationMs: 4000,
    selectedOption: undefined,
  },

  // Frame 4 — Bash Acknowledged (option 1 selected)
  {
    event: buildMockEvent({
      eventType: EventType.TOOL_PERMISSION_REQUEST,
      agentMode: AgentMode.DEFAULT_MODE,
      notificationMessage: 'Claude wants to execute this command',
      toolName: 'Bash',
      notificationContext: buildBashContext(),
    }),
    bubbleType: 'notification',
    statusIcon: { type: 'bash_acknowledged', pulse: false },
    showPause: false,
    showInject: false,
    activity: BASHING_ACTIVITY,
    agentMode: AgentMode.DEFAULT_MODE,
    agentModeTransition: undefined,
    durationMs: 1500,
    selectedOption: 1,
  },

  // Frame 5 — Agent Narrative (research findings)
  {
    event: buildMockEvent({
      eventType: EventType.AGENT_NARRATIVE,
      agentMode: AgentMode.DEFAULT_MODE,
      agentResponse: `Redis is running and all 8 middleware tests pass. The current stack uses Express middleware chaining in \`src/app.ts\`.

I'll use the **sliding window** pattern \u2014 more accurate than fixed windows for burst protection. Let me clarify one detail about the rate limit strategy.`,
    }),
    bubbleType: 'agent_narrative',
    statusIcon: { type: 'processing', pulse: true },
    showPause: true,
    showInject: true,
    activity: WEB_SEARCHING_ACTIVITY,
    agentMode: AgentMode.DEFAULT_MODE,
    agentModeTransition: undefined,
    durationMs: 4500,
    selectedOption: undefined,
  },

  // Frame 6 — Agent Questions
  {
    event: buildMockEvent({
      eventType: EventType.AGENT_QUESTIONS,
      agentMode: AgentMode.DEFAULT_MODE,
      notificationMessage: 'Claude Code needs your attention',
      toolName: 'AskUserQuestion',
      notificationContext: {
        toolName: 'AskUserQuestion',
        toolInput: {
          questions: [
            {
              question: 'How should rate limits apply to authenticated users?',
              header: 'Rate Strategy',
              multiSelect: false,
              options: [
                {
                  label: 'Same limits',
                  description: '100 req/min for all users',
                },
                {
                  label: 'Higher limits',
                  description: 'Authenticated users get 200 req/min (Recommended)',
                },
                {
                  label: 'Exempt',
                  description: 'No rate limiting for authenticated users',
                },
              ],
            },
          ],
        },
        agentDialogue: null,
      },
    }),
    bubbleType: 'questions',
    statusIcon: { type: 'question_blocked', pulse: true },
    showPause: false,
    showInject: false,
    activity: null,
    agentMode: AgentMode.DEFAULT_MODE,
    agentModeTransition: undefined,
    durationMs: 4000,
    selectedOption: undefined,
  },

  // Frame 7 — Question Acknowledged (option 2 selected)
  {
    event: buildMockEvent({
      eventType: EventType.AGENT_QUESTIONS,
      agentMode: AgentMode.DEFAULT_MODE,
      notificationMessage: 'Claude Code needs your attention',
      toolName: 'AskUserQuestion',
      notificationContext: {
        toolName: 'AskUserQuestion',
        toolInput: {
          questions: [
            {
              question: 'How should rate limits apply to authenticated users?',
              header: 'Rate Strategy',
              multiSelect: false,
              options: [
                {
                  label: 'Same limits',
                  description: '100 req/min for all users',
                },
                {
                  label: 'Higher limits',
                  description: 'Authenticated users get 200 req/min (Recommended)',
                },
                {
                  label: 'Exempt',
                  description: 'No rate limiting for authenticated users',
                },
              ],
            },
          ],
        },
        agentDialogue: null,
      },
    }),
    bubbleType: 'questions',
    statusIcon: { type: 'question_acknowledged', pulse: false },
    showPause: false,
    showInject: false,
    activity: null,
    agentMode: AgentMode.DEFAULT_MODE,
    agentModeTransition: undefined,
    durationMs: 1500,
    selectedOption: 2,
  },

  // Frame 8 — Plan Approval
  {
    event: buildMockEvent({
      eventType: EventType.PLAN_APPROVAL_REQUEST,
      agentMode: AgentMode.DEFAULT_MODE,
      notificationMessage: 'Claude Code needs your approval for the plan',
      toolName: 'ExitPlanMode',
      notificationContext: buildPlanContext(),
    }),
    bubbleType: 'notification',
    statusIcon: { type: 'plan_blocked', pulse: true },
    showPause: false,
    showInject: false,
    activity: null,
    agentMode: AgentMode.DEFAULT_MODE,
    agentModeTransition: undefined,
    durationMs: 4000,
    selectedOption: undefined,
  },

  // Frame 9 — Plan Acknowledged (option 2 = auto accept edits) + mode transition
  {
    event: buildMockEvent({
      eventType: EventType.PLAN_APPROVAL_REQUEST,
      agentMode: AgentMode.DEFAULT_MODE,
      notificationMessage: 'Claude Code needs your approval for the plan',
      toolName: 'ExitPlanMode',
      notificationContext: buildPlanContext(),
    }),
    bubbleType: 'notification',
    statusIcon: { type: 'plan_acknowledged', pulse: false },
    showPause: false,
    showInject: false,
    activity: null,
    agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
    agentModeTransition: { from: AgentMode.DEFAULT_MODE, to: AgentMode.AUTO_ACCEPT_EDITS_MODE },
    durationMs: 2000,
    selectedOption: 2,
  },

  // Frame 10 — File Created (Write)
  {
    event: buildMockEvent({
      eventType: EventType.FILE_MODIFICATION,
      agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
      notificationMessage: 'File created: rateLimiter.ts',
      toolName: 'Write',
      fileChanges: buildWriteFileChanges(),
    }),
    bubbleType: 'file_modification',
    statusIcon: { type: 'processing', pulse: true },
    showPause: true,
    showInject: true,
    activity: WRITING_ACTIVITY,
    agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
    agentModeTransition: undefined,
    durationMs: 4500,
    selectedOption: undefined,
  },

  // Frame 11 — File Edited (Edit)
  {
    event: buildMockEvent({
      eventType: EventType.FILE_MODIFICATION,
      agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
      notificationMessage: 'File modified: app.ts',
      toolName: 'Edit',
      fileChanges: buildEditFileChanges(),
    }),
    bubbleType: 'file_modification',
    statusIcon: { type: 'processing', pulse: true },
    showPause: true,
    showInject: true,
    activity: EDITING_ACTIVITY,
    agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
    agentModeTransition: undefined,
    durationMs: 4500,
    selectedOption: undefined,
  },

  // Frame 12 — Agent Reply (summary)
  {
    event: buildMockEvent({
      eventType: EventType.AGENT_REPLY,
      agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
      agentResponse: `Done! Rate limiting is live on \`/api/v1\` endpoints.

**Changes:**
- Created \`src/middleware/rateLimiter.ts\` \u2014 sliding window with Redis \`INCR\`
- Updated \`src/app.ts\` \u2014 wired middleware before route handlers

Authenticated users: **200 req/min**. Anonymous: **100 req/min**. Returns \`429\` with \`Retry-After\` header when exceeded. All 8 middleware tests pass.`,
    }),
    bubbleType: 'agent_narrative',
    statusIcon: { type: 'reply', pulse: false },
    showPause: false,
    showInject: false,
    activity: null,
    agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
    agentModeTransition: undefined,
    durationMs: 5000,
    selectedOption: undefined,
  },
]

export const DEMO_RESET_PAUSE_MS = RESET_PAUSE_MS

/** The last frame index — used for reduced-motion static display. */
export const FINAL_FRAME_INDEX = CONVERSATION_DEMO_FRAMES.length - 1
